import { DateTime, Duration, Interval } from 'luxon';
import classNames from 'classnames';
import { ReactNode, useRef, useState } from 'react';
import { Tooltip } from '../../../components/tooltip/tooltip';

export const TimelineEntry = ({
  className,
  row = 0,
  basisHeightRem = 4,
  start,
  end,
  bufferBefore,
  bufferAfter,
  range,
  title,
  tooltip,
}: {
  className?: string;
  row?: number;
  basisHeightRem?: number;
  start: DateTime;
  end: DateTime;
  bufferBefore?: Duration;
  bufferAfter?: Duration;
  range: Interval;
  title: string;
  tooltip?: ReactNode;
}) => {
  const [hovered, setHovered] = useState<boolean>(false);
  const left = (100.0 * start.diff(range.start).toMillis()) / range.length('milliseconds');
  const right = (100.0 * range.end.diff(end).toMillis()) / range.length('milliseconds');

  const timelineNode = (
    <div
      onMouseLeave={() => setHovered(false)}
      onMouseEnter={() => setHovered(true)}
      className={classNames('timeline-entry vertical-center', className, {
        'overflow-right': right <= 0,
        'overflow-left': left <= 0,
      })}
      style={{
        position: 'absolute',
        top: `${basisHeightRem * (row + 0.125)}rem`,
        left: `${left > 0 ? left : 0}%`,
        right: `${right > 0 ? right : 0}%`,
      }}
    >
      <Tooltip tooltip={tooltip}>
        <div className="timeline-content">
          <span>{start.diff(range.start).milliseconds > 0 ? start.toFormat('t') : ''}</span>
          <span>{title}</span>
          <span>{end.diff(range.end).milliseconds < 0 ? end.toFormat('t') : ''}</span>
        </div>
      </Tooltip>
    </div>
  );

  let bufferNode = undefined;
  if ((bufferBefore?.toMillis() || bufferAfter?.toMillis()) && hovered) {
    const bufferLeft =
      (100.0 *
        start
          .minus(bufferBefore || 0)
          .diff(range.start)
          .toMillis()) /
      range.length('milliseconds');
    const bufferRight = (100.0 * range.end.diff(end.plus(bufferAfter || 0)).toMillis()) / range.length('milliseconds');
    bufferNode = (
      <div
        className={classNames('timeline-entry-buffer', {
          'overflow-right': right <= 0,
          'overflow-left': left <= 0,
        })}
        style={{
          position: 'absolute',
          top: `${basisHeightRem * (row + 0.125)}rem`,
          left: `${bufferLeft > 0 ? bufferLeft : 0}%`,
          right: `${bufferRight > 0 ? bufferRight : 0}%`,
          visibility: hovered ? 'visible' : 'hidden',
        }}
      ></div>
    );
  }

  return (
    <>
      {bufferNode}
      {timelineNode}
    </>
  );
};

export const TimelineUnavailableEntry = ({
  start,
  end,
  range,
}: {
  start?: DateTime;
  end?: DateTime;
  range: Interval;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  if (Interval.fromDateTimes(start || range.start.minus(1), end || range.end.plus(1)).overlaps(range)) {
    const left = start ? (100.0 * start.diff(range.start).toMillis()) / range.length('milliseconds') : 0;
    const right = end ? (100.0 * range.end.diff(end).toMillis()) / range.length('milliseconds') : 0;

    const tooltip = (
      <div className="product-tooltip">
        <div>
          <span>Product Unavailable</span>
        </div>
        <div>
          <span className="tooltip-label">From:</span> {start ? start.toFormat('D') : '-'}
        </div>
        <div>
          <span className="tooltip-label">Until:</span> {end ? end.toFormat('D') : '-'}
        </div>
      </div>
    );

    return (
      <div
        ref={ref}
        className={classNames('timeline-entry unavailable', {
          'overflow-right': right <= 0,
          'overflow-left': left <= 0,
        })}
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: `${left > 0 ? left : 0}%`,
          right: `${right > 0 ? right : 0}%`,
        }}
      >
        <Tooltip tooltip={tooltip}>
          <div
            style={{
              height: `${ref.current?.getBoundingClientRect().height}px`,
              width: `${ref.current?.getBoundingClientRect().width}px`,
            }}
          />
        </Tooltip>
      </div>
    );
  } else {
    return <></>;
  }
};
